<script setup>
import { computed } from 'vue'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import DxTreeView from 'devextreme-vue/tree-view'
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useRoute } from 'vue-router'
import router, { menuRoutes, menuRoutesEnum } from '@/router'
import { getInitials, stringToColor } from '@/lib/utils'

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()

const drawer = ref(false)
const overlay = ref(webSiteStore.sideBarPinned)
const treeView = ref(null)
const menuEntries = webSiteStore.getMenuEntries()
const isInside = ref(false)
const route = useRoute()

const onMouseEnter = () => {
  isInside.value = true
  setTimeout(() => {
    if (isInside.value) {
      webSiteStore.sideBarExpanded = true
      overlay.value = false
    }
  }, 500)
}

const onMouseLeave = () => {
  isInside.value = false
  setTimeout(() => {
    if (!isInside.value) {
      overlay.value = webSiteStore.sideBarPinned
      webSiteStore.sideBarExpanded = false
      if (treeView.value) {
        treeView.value.collapseAll()
      }
    }
  }, 500)
}

const onSideBarPinClick = () => {
  webSiteStore.sideBarPinned = !webSiteStore.sideBarPinned
  overlay.value = webSiteStore.sideBarPinned
}

const saveTreeViewIstance = (e) => {
  treeView.value = e.component
}

const onItemClick = (e) => {
  for (const item of menuEntries) {
    if (item.parent_ref === e.itemData.parent_ref && item.code !== e.itemData.code) {
      treeView.value.collapseItem(item)
    }
  }
}

const onItemExpanded = (e) => {
  if (e.itemData.parent_ref) {
    e.itemElement.classList.remove('dx-state-focused')
    e.itemElement.classList.add('dx-theme-accent-as-text-color')
  }
}

const onItemCollapsed = (e) => {
  e.itemElement.classList.remove('dx-theme-accent-as-text-color')
}

const nodeClass = (item, level = 0) => {
  const classList = selectedMenuEntryCodes.value.includes(item.code) ? ['selected'] : ['grayed-out']
  if (item.parent_ref) {
    classList.push(...nodeClass(findItemByCode(item.parent_ref), level + 1))
  } else {
    switch (level) {
      case 0: classList.push('q-pl-sm'); break
      case 1: classList.push('q-pl-md'); break
      case 2: classList.push('q-pl-xl'); break
      default: classList.push('q-pl-sm'); break
    }
  }
  return classList
}

const findItemByCode = (code) => {
  for (const item of menuEntries) {
    if (item.code === code) {
      return item
    }
  }
  return null
}

const currentMenuEntry = computed(() => menuEntries.find(item => menuRoutes[item.code] === route.path))

const selectedMenuEntryCodes = computed(() => {
  let entry = currentMenuEntry.value
  const codes = [entry?.code]
  while (entry?.parent_ref) {
    codes.push(entry.parent_ref)
    entry = findItemByCode(entry.parent_ref)
  }
  return codes
})

const onMenuEntryClick = (item, forceTargetBlank = false) => {
  if (item.url) {
    // add access token to the url
    window.open(item.url + '?token=' + webSiteStore.accessToken, item.target || '_blank')
  } else if (item.action) {
    item.action({targetBlank: forceTargetBlank})
  } else if (menuRoutes[item.code]) {
    forceTargetBlank ? window.open(menuRoutes[item.code], '_blank') : router.push(menuRoutes[item.code])
  }
}

const navigateToCompany = () => { router.push(menuRoutesEnum.COMPANY) }

const companyAddressStreet = computed(() => {
  const companyData = registryStore.companyData
  return companyData?.address && companyData.address.street ? `${companyData.address.street} - ${companyData.address.zip_code}` : ''
})
const companyAddressCity = computed(() => {
  const companyData = registryStore.companyData
  return companyData?.address && companyData.address.city_name ? `${companyData.address.city_name} (${companyData.address.state_code})` : ''
})

const avatarInitials = computed(() => getInitials(registryStore.companyData?.name))
const avatarStyle = computed(() => ({ 'background-color': `${stringToColor(avatarInitials.value)}` }))

</script>
<template>
  <div>
    <q-drawer
      v-model="drawer"
      class="nav bg-grey-2"
      show-if-above
      bordered
      :mini="overlay"
      :width="250"
      :breakpoint="500"
      :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <q-list class="nav-tree">
        <q-item
          v-ripple
          class="menu-item company-info"
          clickable
        >
          <q-item-section
            v-if="overlay"
            avatar
          >
            <q-avatar
              :style="avatarStyle"
              size="2rem"
            >
              {{ avatarInitials }}
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label class="company-name">
              {{ registryStore.companyData?.name }}
            </q-item-label>
            <q-item-label
              v-if="companyAddressStreet || companyAddressCity"
              caption
            >
              <div v-if="companyAddressStreet">
                {{ companyAddressStreet }}
              </div>
              <div v-if="companyAddressCity">
                {{ companyAddressCity }}
              </div>
            </q-item-label>
          </q-item-section>
          <q-item-section
            side
          >
            <q-icon
              name="mdi-pencil"
              class="external-link"
              @click.stop="navigateToCompany"
            />
          </q-item-section>
        </q-item>
        <q-separator />
        <dx-tree-view
          key-expr="code"
          data-structure="plain"
          parent-id-expr="parent_ref"
          expand-event="click"
          collapse-icon="none"

          :data-source="menuEntries"
          @initialized="saveTreeViewIstance"
          @item-click="onItemClick"
          @item-expanded="onItemExpanded"
          @item-collapsed="onItemCollapsed"
        >
          <template #item="data">
            <q-item
              id="nav-item"
              v-ripple
              class="menu-item"
              clickable
              :class="[nodeClass(data.data)]"
              @click="onMenuEntryClick(data.data)"
            >
              <q-item-section
                avatar
              >
                <img
                  v-if="data.data.customIcon"
                  :src="data.data.customIcon"
                  class="q-icon"
                />
                <q-icon
                  v-else
                  :name="data.data.icon"
                />
              </q-item-section>

              <q-item-section>
                {{ i18n.t(data.data.code) }}
              </q-item-section>
              <q-item-section
                v-if="data.data.url || data.data.route || data.data.action"
                side
              >
                <q-icon
                  name="mdi-open-in-new"
                  class="external-link"
                  @click.stop="onMenuEntryClick(data.data, true)"
                />
              </q-item-section>
            </q-item>
          </template>
        </dx-tree-view>
      </q-list>

      <q-list
        v-if="drawer"
        class="z-top"
      >
        <q-separator />
        <q-item
          v-ripple
          clickable
          @click="onSideBarPinClick"
        >
          <q-item-section avatar>
            <q-icon :name="webSiteStore.sideBarPinned ? 'mdi-pin' : 'mdi-pin-off'" />
          </q-item-section>

          <q-item-section>
            <span v-if="webSiteStore.sideBarPinned">{{ i18n.t('common.pin') }}</span>
            <span v-else>{{ i18n.t('common.unPin') }}</span>
          </q-item-section>
        </q-item>
      </q-list>
    </q-drawer>
  </div>
</template>
<style lang="scss">
.nav {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.nav-tree {
  overflow: hidden;
}
.nav .dx-treeview-node {
  padding: 0;
}
.nav .dx-treeview-item {
  padding-top: 0;
  padding-bottom: 0;
  padding-right: 0;

  & .dx-treeview-expander-icon-stub{
    display: none ;
  }
  & .dx-treeview-custom-expand-icon {
    display: none;
  }
  & .dx-treeview-custom-collapse-icon {
    display: none;
  }
}
.q-drawer--mini .dx-treeview-item {
  padding: 0 !important;
}
.selected {
  color: $primary !important;
}
.grayed-out {
  color: $grey-9 !important;
}
.external-link {
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.5s ease-in-out;
  font-size: 1rem !important;
  width: 1rem;
  &:hover {
    color: $primary;
  }
}
.menu-item {
  width: 100%;
}
.menu-item:hover .external-link {
  opacity: 1;
}
.company-name {
  font-size: larger
}
</style>
